'use client';

import { useEffect, useMemo, useRef, useState } from 'react';

import {
  PREDEFINED_SOCIAL_PROFILES,
  type PredefinedSocialType,
} from '@web/constants';
import { InlineButton, PlusIcon, SmartInput, SocialProfileIcon } from '@web/ui';
import { normalizeFormFieldValues, normalizeUrl } from '@web/utils';

import styles from './SocialProfilesFormSection.module.scss';

export type CustomSocialLinkFormValue = {
  label: string;
  url: string;
};

export type SocialProfilesFormValue = {
  predefined: Record<PredefinedSocialType, string>;
  custom: CustomSocialLinkFormValue[];
};

export function SocialProfilesFormSection({
  value,
  onChange,
  customCountName = 'custom-social-count',
  errors,
}: {
  /**
   * * Controlled mode (optional). If omitted, the component works in uncontrolled
   * * mode (recommended for large forms to avoid keystroke lag).
   */
  value?: SocialProfilesFormValue;
  onChange?: (next: SocialProfilesFormValue) => void;
  /**
   * * Hidden input name that stores how many custom social rows exist,
   * * so parent forms can read via FormData on submit.
   */
  customCountName?: string;
  /**
   * Optional field-level errors keyed by input `name`.
   * Example: { 'social-linkedin': 'Enter a valid URL.' }
   */
  errors?: Record<string, string | undefined>;
}) {
  const isControlled = Boolean(value && onChange);

  const controlledCustomCount = value?.custom?.length ?? 0;
  const [uncontrolledCustomCount, setUncontrolledCustomCount] = useState(0);

  const customCount = isControlled
    ? controlledCustomCount
    : uncontrolledCustomCount;

  const controlledPredefined = value?.predefined;
  const emptyPredefined = useMemo(
    () =>
      PREDEFINED_SOCIAL_PROFILES.reduce(
        (acc, s) => {
          acc[s.type] = '';
          return acc;
        },
        {} as Record<PredefinedSocialType, string>,
      ),
    [],
  );

  const predefinedValues = controlledPredefined ?? emptyPredefined;

  const sectionRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const form = el.closest('form');
    if (!form) return;

    const onSubmitCapture = () => {
      if (isControlled) return;

      normalizeFormFieldValues(
        el,
        'input[name^="social-"], textarea[name^="social-"], input[name^="custom-social-url-"], textarea[name^="custom-social-url-"]',
        (v) => normalizeUrl(v, { defaultProtocol: 'https://' }),
      );
    };

    form.addEventListener('submit', onSubmitCapture, true);
    return () => form.removeEventListener('submit', onSubmitCapture, true);
  }, [isControlled]);

  return (
    <section
      ref={(node) => {
        sectionRef.current = node;
      }}
      className={styles.section}
      aria-label="Social Profiles section"
    >
      <h2 className={styles.sectionTitle}>Social Profiles</h2>

      {/* // * Used by parent <form> submit parsing */}
      <input type="hidden" name={customCountName} value={customCount} />

      <div className={styles.rows}>
        {PREDEFINED_SOCIAL_PROFILES.map((s) => (
          <div key={s.type} className={styles.row}>
            <div
              className={styles.rowLabel}
              aria-label={s.label}
              title={s.label}
            >
              <SocialProfileIcon type={s.type} />
            </div>
            <SmartInput
              type="text"
              name={`social-${s.type}`}
              label={`${s.label} URL or handle`}
              error={errors?.[`social-${s.type}`]}
              {...(isControlled
                ? {
                    value: predefinedValues[s.type],
                    onChange: (v: string) =>
                      onChange?.({
                        ...(value as SocialProfilesFormValue),
                        predefined: { ...predefinedValues, [s.type]: v },
                      }),
                  }
                : {
                    defaultValue: predefinedValues[s.type],
                  })}
            />
          </div>
        ))}

        {Array.from({ length: customCount }).map((_, idx) => (
          <div key={`custom-social-${idx}`} className={styles.customRow}>
            <SmartInput
              type="text"
              name={`custom-social-name-${idx}`}
              label="Social name"
              error={errors?.[`custom-social-name-${idx}`]}
              {...(isControlled
                ? {
                    value: value?.custom?.[idx]?.label ?? '',
                    onChange: (v: string) =>
                      onChange?.({
                        ...(value as SocialProfilesFormValue),
                        custom: (value?.custom ?? []).map((row, i) =>
                          i === idx ? { ...row, label: v } : row,
                        ),
                      }),
                  }
                : { defaultValue: '' })}
            />

            <SmartInput
              type="text"
              name={`custom-social-url-${idx}`}
              label="URL or handle"
              error={errors?.[`custom-social-url-${idx}`]}
              {...(isControlled
                ? {
                    value: value?.custom?.[idx]?.url ?? '',
                    onChange: (v: string) =>
                      onChange?.({
                        ...(value as SocialProfilesFormValue),
                        custom: (value?.custom ?? []).map((row, i) =>
                          i === idx ? { ...row, url: v } : row,
                        ),
                      }),
                  }
                : { defaultValue: '' })}
            />
          </div>
        ))}

        <InlineButton
          ariaLabel="Add custom social"
          onClick={() => {
            if (isControlled) {
              onChange?.({
                ...(value as SocialProfilesFormValue),
                custom: [
                  ...((value as SocialProfilesFormValue).custom ?? []),
                  { label: '', url: '' },
                ],
              });
              return;
            }

            setUncontrolledCustomCount((c) => c + 1);
          }}
          style={{
            width: 'fit-content',
          }}
        >
          <PlusIcon /> New Link
        </InlineButton>
      </div>
    </section>
  );
}
